'use client'

import { useState, useRef, useEffect, type MouseEvent } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import Sheet from '@/components/ui/Sheet'
import Link from '@/components/ui/Link'
import { Icon } from '@/icons/Icon'
import { cn } from '@/lib/utils'
import { easeMedium } from '@/lib/transitions'
import { type MenuItem } from '@/helpers/menus'

type MobileMenuProps = {
  items: MenuItem[]
  children: React.ReactNode
  menuVisible?: boolean
  setMenuVisibleAction?: (value: boolean) => void
}

type Direction = 'forward' | 'back'

export default function MobileMenu({
  items,
  children,
  menuVisible: externalMenuVisible,
  setMenuVisibleAction: externalSetMenuVisible,
}: MobileMenuProps) {
  const [internalMenuVisible, setInternalMenuVisible] = useState(false)

  const isControlled = externalMenuVisible !== undefined && externalSetMenuVisible !== undefined
  const menuVisible = isControlled ? externalMenuVisible : internalMenuVisible
  const setMenuVisible = isControlled ? externalSetMenuVisible : setInternalMenuVisible
  const [currentMenu, setCurrentMenu] = useState<MenuItem[]>(items)
  const [menuStack, setMenuStack] = useState<MenuItem[][]>([items])
  const [direction, setDirection] = useState<Direction>('forward')
  const [copied, setCopied] = useState(false)
  const menuStackRef = useRef(menuStack)

  // Keep ref in sync with state
  useEffect(() => {
    menuStackRef.current = menuStack
  }, [menuStack])

  // Sync with items prop changes
  useEffect(() => {
    setCurrentMenu(items)
    setMenuStack([items])
  }, [items])

  const handleItemClick = (item: MenuItem) => {
    if (item.disabled) return

    // Handle custom components
    if (item.custom) {
      const customMenu: MenuItem[] = [
        {
          label: item.subTitle ?? 'Back',
          icon: 'arrow-left',
          onClick: () => goBack(),
          back: true,
        },
        {
          label: 'Divider',
          icon: <></>,
        },
        {
          label: '',
          icon: <></>,
          custom: item.custom,
        },
      ]
      setDirection('forward')
      setMenuStack([...menuStack, customMenu])
      setCurrentMenu(customMenu)
      return
    }

    // Handle submenu
    if (item.sub && item.sub.length > 0) {
      const subMenu: MenuItem[] = [
        {
          label: item.subTitle ?? 'Back',
          icon: 'arrow-left',
          onClick: () => goBack(),
          back: true,
        },
        {
          label: 'Divider',
          icon: <></>,
        },
        ...item.sub,
      ]
      setDirection('forward')
      setMenuStack([...menuStack, subMenu])
      setCurrentMenu(subMenu)
      return
    }

    // Handle onClick
    if (item.onClick) {
      item.onClick()
      if (item.simultaneousClick) {
        item.simultaneousClick()
      }
      // Don't close menu if it's the Back button
      if (!item.back) {
        setMenuVisible(false)
        resetMenu()
      }
      return
    }

    // Handle share (copy link)
    if (item.label === 'Share' && item.link) {
      copyLink(item.link)
      return
    }

    // Handle link navigation - let Link component handle it
    if (item.link) {
      // simultaneousClick and menu closing will be handled in the Link onClick
      return
    }
  }

  const goBack = () => {
    if (menuStackRef.current.length > 1) {
      setDirection('back')
      const newStack = menuStackRef.current.slice(0, -1)
      setMenuStack(newStack)
      setCurrentMenu(newStack[newStack.length - 1])
    }
  }

  const resetMenu = () => {
    setTimeout(() => {
      setMenuStack([items])
      setCurrentMenu(items)
    }, 300)
  }

  const copyLink = (link?: string) => {
    if (!link) return
    const fullLink = link.startsWith('http') ? link : `${window.location.origin}${link}`
    navigator.clipboard.writeText(fullLink)
    setCopied(true)
    setTimeout(() => {
      setCopied(false)
    }, 2000)
  }

  const variants = {
    initial: (dir: Direction) => ({
      x: dir === 'forward' ? '100%' : '-100%',
    }),
    target: {
      x: '0%',
    },
    exit: (dir: Direction) => ({
      x: dir === 'forward' ? '-100%' : '100%',
    }),
  }

  const renderMenuItems = (menu: MenuItem[]) => {
    return menu.map((item, index) => {
      const isCustom = item.custom
      const hasSub = item.sub && item.sub.length > 0

      if (item.label === 'Divider') {
        return (
          <li key={`divider-${index}`} className="relative z-10 w-full py-1.5">
            <hr className="h-px w-full border-0 bg-gray-200 shadow-none dark:bg-white/10"></hr>
          </li>
        )
      }

      // Render custom component inline when it's purely a display item (no label, no actions)
      if (isCustom && !item.label && !item.onClick && !item.link && !hasSub) {
        return (
          <li key={index} className="w-full px-4 py-2">
            {item.custom}
          </li>
        )
      }

      const content = (
        <div
          className={cn(
            'flex w-full items-center gap-3.5 whitespace-nowrap rounded-lg px-3 py-2.5 transition-colors [&>p>span]:font-medium',
            item.disabled ? 'cursor-default select-none opacity-50' : `cursor-pointer`,
          )}
        >
          <AnimatePresence mode="wait">
            <motion.span
              key={copied && item.label === 'Share' ? 'copied' : 'default'}
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0 }}
              transition={{ duration: 0.1 }}
              className="align-center flex shrink-0 justify-center text-zinc-400"
            >
              {copied && item.label === 'Share' ? (
                <Icon name="check" />
              ) : typeof item.icon === 'string' ? (
                <Icon name={item.icon} />
              ) : (
                item.icon
              )}
            </motion.span>
          </AnimatePresence>
          <p className="flex flex-1 gap-[5px] truncate text-16 text-zinc-500">
            {copied && item.label === 'Share' ? 'Link copied' : item.label}
          </p>
          {item.selected && <Icon name="check" className="ml-auto text-primary-500" />}
          {(hasSub || isCustom) && <Icon name="caret-right" size={10} className="ml-auto" />}
        </div>
      )

      const liClassName = cn(
        'group w-full px-2 py-0.5',
        index === 0 && 'pt-2',
        index === menu.length - 1 && 'pb-2',
      )

      // Wrap with Link if item has a link and is not Share
      if (item.link && item.label !== 'Share') {
        const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
          if (item.onClick) {
            item.onClick()
          }
          if (item.simultaneousClick) {
            item.simultaneousClick()
          }
          if (
            item.onClick &&
            (item.link?.startsWith('#') || item.link?.startsWith(`${window.location.pathname}#`))
          ) {
            event.preventDefault()
          }
          setMenuVisible(false)
          resetMenu()
        }

        return (
          <li key={index} className={liClassName}>
            <Link
              href={item.link}
              onClick={handleClick}
              className="w-full"
              passReferrerUrl={item.passReferrerUrl}
              sponsored={item.sponsored}
            >
              {content}
            </Link>
          </li>
        )
      }

      return (
        <li key={index} onClick={() => handleItemClick(item)} className={liClassName}>
          {content}
        </li>
      )
    })
  }

  return (
    <Sheet
      trigger={
        <div className="relative" onClick={() => setMenuVisible(true)}>
          {children}
        </div>
      }
      isVisible={menuVisible}
      isMenu={true}
      close={() => {
        setMenuVisible(false)
        resetMenu()
      }}
    >
      <div className="relative overflow-hidden">
        {/* Invisible content for height measurement */}
        <div className="invisible flex flex-col">{renderMenuItems(currentMenu)}</div>
        {/* Visible animated content */}
        <AnimatePresence initial={false} custom={direction}>
          <motion.ul
            key={menuStack.length}
            variants={variants}
            custom={direction}
            initial="initial"
            animate="target"
            exit="exit"
            transition={easeMedium}
            className="absolute inset-0 flex flex-col"
          >
            {renderMenuItems(currentMenu)}
          </motion.ul>
        </AnimatePresence>
      </div>
    </Sheet>
  )
}
